import { useState, useRef, useMemo, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardCheck,
  BarChart3,
  Bell,
  LifeBuoy,
  Users,
  MonitorPlay,
  LayoutDashboard,
} from 'lucide-react';

import { Navigation, type NavItem } from '@/components/layout/Navigation';
import { UserProfile } from '@/components/layout/UserProfile';
import {
  Sidebar as UISidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useCanManageUsers } from '@/pages/users/components/useUserManagement';
import { getMyNotifications, markNotificationsSeen, type InAppNotification } from '@/api/users';
import { formatRelativeTime } from '@/utils/date';
import { usePermissions } from '@/hooks/usePermissions';
import { useCanOpenContentTracker } from '@/hooks/useAppAccess';

const USER_MANAGER_ITEM: NavItem = { title: 'Users', path: '/admin/users', icon: Users };
const MOCKUPS_ITEM: NavItem = { title: 'Mockups', path: '/mockups', icon: MonitorPlay };
const DASHBOARD_ITEM: NavItem = {
  title: 'Dashboard',
  path: '/dashboard',
  icon: LayoutDashboard,
};

export function Sidebar({ ...props }: React.ComponentProps<typeof UISidebar>) {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const { data: canManageUsers } = useCanManageUsers();
  const { data: permissions } = usePermissions();
  const queryClient = useQueryClient();

  // The ADMIN section mixes two independent authorization sources: Users and
  // Mockups come from the user-manager allowlist (APP_USER_MANAGERS), while
  // Dashboard comes from the role-based `can_view_dashboard` permission.
  const adminItems = useMemo(() => {
    const items: NavItem[] = [];
    if (canManageUsers) items.push(USER_MANAGER_ITEM);
    if (permissions?.can_view_dashboard) items.push(DASHBOARD_ITEM);
    if (canManageUsers) items.push(MOCKUPS_ITEM);
    return items;
  }, [canManageUsers, permissions?.can_view_dashboard]);

  // Notification state
  const [notifOpen, setNotifOpen] = useState(false);
  const notifBtnRef = useRef<HTMLDivElement>(null);
  // The endpoint 403s without the content_tracker grant; don't poll it into a wall.
  const { allowed: canOpenContentTracker } = useCanOpenContentTracker();

  const { data: notificationsData } = useQuery({
    queryKey: ['my-notifications'],
    queryFn: () => getMyNotifications(30),
    enabled: canOpenContentTracker,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const notifications = useMemo(
    () => notificationsData?.notifications ?? [],
    [notificationsData?.notifications],
  );
  const lastSeenAt = notificationsData?.last_seen_at ?? null;

  const unreadCount = lastSeenAt
    ? notifications.filter((n) => n.created_at > lastSeenAt).length
    : notifications.length;

  const handleNotifToggle = useCallback(() => {
    setNotifOpen((prev) => {
      if (!prev && notifications.length > 0) {
        markNotificationsSeen()
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['my-notifications'] });
          })
          .catch(() => {
            // Silently ignore
          });
      }
      return !prev;
    });
  }, [notifications, queryClient]);

  // Event types that should open the status modal overlay
  const STATUS_MODAL_EVENTS = useMemo(
    () =>
      new Set([
        'preview_link_added',
        'preview_feedback_added',
        'preview_approved',
        'preview_expiry_reminder_sent',
        'comment_added',
      ]),
    [],
  );

  const handleClickNotification = useCallback(
    (notification: InAppNotification) => {
      setNotifOpen(false);

      // Deep link via link_path/link_param/link_value if present
      if (notification.link_path && notification.link_param && notification.link_value) {
        window.location.href = `${notification.link_path}?${notification.link_param}=${encodeURIComponent(notification.link_value)}`;
        return;
      }

      // Path-based deep link (e.g., /campaigns/{uuid}/preview)
      if (notification.link_path) {
        window.location.href = notification.link_path;
        return;
      }

      // Status modal deep link for specific event types
      if (notification.campaign_id && STATUS_MODAL_EVENTS.has(notification.event_type)) {
        window.location.href = `/?campaign=${encodeURIComponent(notification.campaign_id)}&action=status`;
        return;
      }

      // Fallback: navigate to campaign row (highlight only, no modal)
      if (notification.campaign_id) {
        window.location.href = `/?campaign=${encodeURIComponent(notification.campaign_id)}`;
      }
    },
    [STATUS_MODAL_EVENTS],
  );

  // Get the vertical position of the bell button for the fixed dropdown
  const notifBtnTop = notifBtnRef.current?.getBoundingClientRect().top ?? 0;
  const sidebarRight =
    notifBtnRef.current?.closest('[data-sidebar]')?.getBoundingClientRect().right ?? 0;

  const navItems: NavItem[] = [
    { title: 'Help & Support', path: '/about', icon: LifeBuoy },
    { title: 'Plan Approval', path: '/media-plan-approval', icon: ClipboardCheck },
    {
      title: 'Content Tracker',
      path: '/content-tracker',
      icon: BarChart3,
      matchPrefixes: ['/campaigns/'],
    },
    {
      title: 'Notifications',
      path: '',
      icon: Bell,
      badge: unreadCount > 0 ? unreadCount : undefined,
      onClick: handleNotifToggle,
      ref: notifBtnRef,
    },
  ];

  return (
    <>
      <motion.div
        className="relative z-[1] bg-white"
        initial={{ x: -280, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <UISidebar collapsible="icon" {...props}>
          <SidebarHeader className="py-1">
            <div className={`py-2 flex justify-center ${isCollapsed ? 'px-0' : 'px-2'}`}>
              <Link
                to="/"
                className="flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <AnimatePresence mode="popLayout" initial={false}>
                  {isCollapsed ? (
                    <motion.div
                      key="icon"
                      className="h-7 w-7 rounded-md overflow-hidden"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                    >
                      <img src="/WPPO.png" alt="WPP Open" className="h-full w-full object-cover" />
                    </motion.div>
                  ) : (
                    <motion.img
                      key="full"
                      src="/wpp-open-logo.png"
                      alt="WPP Open"
                      className="h-7 flex-shrink-0"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                    />
                  )}
                </AnimatePresence>
              </Link>
            </div>
          </SidebarHeader>
          {/* Divider below header */}
          <div className="h-px w-full bg-slate-200 shrink-0" />
          <SidebarContent className="flex flex-col flex-grow min-h-0 p-0 overflow-x-hidden">
            <Navigation items={navItems} sectionLabel="NAVIGATION" />
            {adminItems.length > 0 && (
              <div className={isCollapsed ? 'mt-3' : ''}>
                <Navigation items={adminItems} sectionLabel="ADMIN" />
              </div>
            )}
          </SidebarContent>
          {/* Divider above footer */}
          <div className="h-px w-full bg-slate-200 shrink-0" />
          <SidebarFooter>
            <UserProfile />
          </SidebarFooter>
          <SidebarRail />
        </UISidebar>
      </motion.div>

      {/* Notification dropdown — rendered outside sidebar to escape overflow clipping */}
      {notifOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[60]"
            aria-hidden
            onClick={() => setNotifOpen(false)}
          />
          <div
            className="fixed z-[70] w-80 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
            style={{ top: notifBtnTop, left: sidebarRight + 8 }}
          >
            {/* Header */}
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Notifications
              </p>
            </div>

            {/* Notification list */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-slate-300" />
                  </div>
                  <p className="text-sm text-slate-400">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const isUnread = lastSeenAt ? notification.created_at > lastSeenAt : true;
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      className={`block w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${isUnread ? 'bg-[#0014cc]/[0.03]' : ''}`}
                      onClick={() => handleClickNotification(notification)}
                    >
                      <div className="flex items-start gap-2.5">
                        {isUnread && (
                          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-[#0014cc]" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800">{notification.title}</p>
                          <p className="mt-0.5 text-xs text-slate-600 line-clamp-2">
                            {notification.item_text}
                          </p>
                          <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-slate-400">
                            {notification.campaign_id && (
                              <>
                                <span className="truncate font-medium">
                                  {notification.campaign_id}
                                </span>
                                <span>&middot;</span>
                              </>
                            )}
                            {notification.actor_name && (
                              <>
                                <span className="truncate">{notification.actor_name}</span>
                                <span>&middot;</span>
                              </>
                            )}
                            <span className="flex-shrink-0">
                              {formatRelativeTime(notification.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {notificationsData && notificationsData.total_count > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 px-4 py-2">
                <p className="text-center text-[10px] text-slate-400">
                  <span className="font-semibold text-slate-500">
                    {notificationsData.total_count}
                  </span>{' '}
                  notification{notificationsData.total_count !== 1 ? 's' : ''} total
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
